import React, {useCallback, useEffect, useState} from 'react';
import {withTranslation} from 'react-i18next';
import PropTypes from 'prop-types';
import SideNavLayout from '../../../layouts/SideNavLayout';
import {DriverTourService} from "../../../services/backend/driverTourService";
import {DriverTourDetail, DriverTours, LoadingWrapper} from "../../../components";
import {ShipperQueryService} from "../../../services/backend/shipperQueryService";
import {ClientService} from "../../../services/client/clientService";
import {clientTypes} from "../../../services/client/clientTypes";

const DriverViews = {
    TOURS: 0,
    TOUR_DETAIL: 1,
};

const shipperClient = ClientService.getClient(clientTypes.shipper);

function DriverTourOverview(props) {

    const {t} = props;

    const [backendLoadingTours, setBackendLoadingTours] = useState(false);
    const [tours, setTours] = useState([]);
    const [view, setView] = useState(DriverViews.TOURS);
    const [selectedTour, setSelectedTour] = useState(null);
    const [selectedStop, setSelectedStop] = useState(null);
    const [boxTypesAllShipper, setBoxTypesAllShipper] = useState({});

    const loadTours = useCallback(async () => {
        setBackendLoadingTours(true);
        try {
            const response = await DriverTourService.getToursForDriver();
            setTours(response.data);
            return response.data;
        } catch (error) {
            alert(t('errorLoadingTours'))
            return [];
        } finally {
            setBackendLoadingTours(false);
        }
    }, [t]);

    useEffect(() => {
        loadTours();
    }, [loadTours]);
    

    useEffect(() => {
        const allShipperNames = [...new Set(tours.flatMap(tour =>
            tour.stops.map(stop => stop.shipperName)
        ))];

        allShipperNames.forEach(async (shipperName) => {
            if (shipperName && !boxTypesAllShipper[shipperName]) {
                try {
                    const response = await shipperClient.query({
                        query: ShipperQueryService.getBoxTypesByShipperNameAndIdentifierList(shipperName, [])
                    });

                    const types = response.data?.boxTypes || [];

                    setBoxTypesAllShipper(prev => ({
                        ...prev,
                        [shipperName]: types
                    }));
                } catch (err) {
                    alert(t('errorLoadingBoxTypes'));
                }
            }
        });
    }, [tours, boxTypesAllShipper, t]);

    const showTourDetail = (tour) => {
        setSelectedTour(tour);
        setView(DriverViews.TOUR_DETAIL);
    };

    const goBackToTours = () => {
        setSelectedTour(null);
        setView(DriverViews.TOURS);
    };

    const saveEditedStopProducts = async (updatedStops) => {
        try {
            await DriverTourService.updateProductsOnStops(updatedStops);
        } catch (e) {
            alert(t('errorUpdatingStop'));
            return;
        }
        
        try{
            const newTours = await loadTours();
            
            if (selectedTour) {
                const updatedTour = newTours.find(t => t.tourId === selectedTour.tourId);
                if (updatedTour) {
                    setSelectedTour(updatedTour);

                    if (selectedStop) {
                        const updatedStop = updatedTour.stops.find(s => s.tourStopId === selectedStop.tourStopId);
                        if (updatedStop) {
                            setSelectedStop(updatedStop);
                        }
                    }
                }
            }
        } catch (e) {
            alert(t('errorLoadingTours'));
        }
    };

    return (
        <SideNavLayout title={t('processFullGoods')}>
            <LoadingWrapper loading={backendLoadingTours}>
                {(() => {
                    switch (view) {
                        case DriverViews.TOURS:
                            return (
                                <DriverTours
                                    tours={tours}
                                    onSelectTour={showTourDetail}
                                />
                            );
                        case DriverViews.TOUR_DETAIL:
                            return (
                                <DriverTourDetail
                                    tour={selectedTour}
                                    saveEditedStopProducts={saveEditedStopProducts}
                                    boxTypesAllShipper={boxTypesAllShipper}
                                    onBack={goBackToTours}
                                />
                            );
                        default:
                            return null;
                    }
                })()}
            </LoadingWrapper>
        </SideNavLayout>
    );
}

DriverTourOverview.propTypes = {
    t: PropTypes.func.isRequired,
};

export default withTranslation()(DriverTourOverview);